import { supabase } from '../config/supabase';

export interface Profile {
  id: string;
  name: string;
  email: string;
  is_supervisor?: boolean;
  role?: string;
}

export interface SessionLog {
  id: string;
  user_id: string;
  os_name: string;
  os_version: string;
  browser_name: string;
  browser_version: string;
  ip_address: string;
  device_type: string;
  login_timestamp: string;
  logout_timestamp: string | null;
  session_invalidated: boolean | null;
  location: any;
  created_at: string;
  updated_at: string;
  profiles: Profile;
}

interface SessionWithProfile {
  user_id: string;
  profiles: {
    email: string;
    role: string;
  };
}

export interface SessionFilters {
  userId?: string;
  startDate?: string;
  endDate?: string;
  deviceType?: string;
  osName?: string;
  browserName?: string;
  sessionStatus?: 'active' | 'closed' | 'all';
  page: number;
  pageSize: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
}

export const sessionService = {
  async createSessionLog(sessionData: Partial<SessionLog>) {
    const { data, error } = await supabase
      .from('session_logs')
      .insert(sessionData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateLogoutTimestamp(sessionId: string) {
    try {
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from('session_logs')
        .update({ 
          logout_timestamp: timestamp,
          updated_at: timestamp
        })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) {
        console.error('[SessionService] Error updating logout timestamp:', error);
        throw error;
      }
      
      console.log('[SessionService] Successfully updated session:', data);
      return data;
    } catch (error) {
      console.error('[SessionService] Error in updateLogoutTimestamp:', error);
      throw error;
    }
  },

  async getActiveSessions() {
    const { data, error } = await supabase
      .from('session_logs')
      .select(`
        *,
        profiles (
          id,
          name,
          email
        )
      `)
      .is('logout_timestamp', null);

    if (error) throw error;
    return data || [];
  },

  async getSessionLogs(filters: SessionFilters): Promise<PaginatedResponse<SessionLog>> {
    try {
      let query = supabase
        .from('session_logs')
        .select(`
          *,
          profiles (
            id,
            name,
            email
          )
        `, { count: 'exact' });

      // Aplicar filtros
      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters.startDate) {
        query = query.gte('login_timestamp', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('login_timestamp', filters.endDate);
      }

      if (filters.deviceType) {
        query = query.eq('device_type', filters.deviceType);
      }

      if (filters.osName) {
        query = query.eq('os_name', filters.osName);
      }

      if (filters.browserName) {
        query = query.eq('browser_name', filters.browserName);
      }

      if (filters.sessionStatus === 'active') {
        query = query.is('logout_timestamp', null);
      } else if (filters.sessionStatus === 'closed') {
        query = query.not('logout_timestamp', 'is', null);
      }

      // Calcular rango para paginación
      const from = filters.page * filters.pageSize;
      const to = from + filters.pageSize - 1;

      // Aplicar ordenamiento y paginación
      query = query
        .order('login_timestamp', { ascending: false })
        .range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        count: count || 0
      };
    } catch (error) {
      console.error('Error fetching session logs:', error);
      throw error;
    }
  },

  async forceLogout(sessionId: string) {
    try {
      // 1. Obtener la información de la sesión actual
      const { data: sessionData, error: fetchError } = await supabase
        .from('session_logs')
        .select(`
          id,
          user_id,
          profiles!inner (
            email
          )
        `)
        .eq('id', sessionId)
        .single() as { data: SessionWithProfile | null, error: any };

      if (fetchError) throw fetchError;
      if (!sessionData) throw new Error('Session not found');

      // 2. Verificar que el usuario actual es supervisor
      const {
        data: { user },
        error: authError
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) throw new Error('No authenticated user');

      const { data: currentUser, error: userError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (currentUser.role !== 'supervisor') {
        throw new Error('Only supervisors can force logout');
      }

      console.log(`[SessionService] Supervisor ${user.email} está forzando el cierre de sesión del usuario ${sessionData.profiles.email}`);

      // 3. Actualizar el registro de sesión y marcar como inválida
      const timestamp = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('session_logs')
        .update({ 
          logout_timestamp: timestamp,
          updated_at: timestamp,
          session_invalidated: true
        })
        .eq('id', sessionId)
        .select();

      if (updateError) {
        console.error('[SessionService] Error al actualizar la sesión:', updateError);
        throw updateError;
      }

      console.log(`[SessionService] Sesión ${sessionId} marcada como inválida para el usuario ${sessionData.profiles.email}`);
      return true;
    } catch (error) {
      console.error('Error en forceLogout:', error);
      throw error;
    }
  }
};
